import React, { createContext, useContext, ReactNode } from 'react';
import { ServiceContainer } from '../../domain/ports/ServiceContainer';

// We only import the interface, not concrete implementations
// The concrete implementation will be injected at runtime
interface ServicesContextType {
  serviceContainer: ServiceContainer;
}

const ServicesContext = createContext<ServicesContextType | undefined>(undefined);

interface ServicesProviderProps {
  children: ReactNode;
  serviceContainer: ServiceContainer;
}

export const ServicesProvider: React.FC<ServicesProviderProps> = ({ 
  children, 
  serviceContainer 
}) => {
  const services: ServicesContextType = {
    serviceContainer
  };

  return (
    <ServicesContext.Provider value={services}>
      {children}
    </ServicesContext.Provider>
  );
};

export const useServices = (): ServicesContextType => {
  const context = useContext(ServicesContext);
  if (context === undefined) {
    throw new Error('useServices must be used within a ServicesProvider');
  }
  return context;
};

export const useServiceContainer = (): ServiceContainer => {
  return useServices().serviceContainer;
};

// Convenience hooks for accessing individual services
export const useAccountPort = () => useServiceContainer().accountPort;
export const useAlertManagementPort = () => useServiceContainer().alertManagementPort;
export const useDataPort = () => useServiceContainer().dataPort;
export const useLocationManagementPort = () => useServiceContainer().locationManagementPort;
export const useOrganizationManagementPort = () => useServiceContainer().organizationManagementPort;
export const useOrganizationAddressBookPort = () => useServiceContainer().organizationAddressBookPort;
export const useSensorManagementPort = () => useServiceContainer().sensorManagementPort;
export const useUserManagementPort = () => useServiceContainer().userManagementPort;

// Repository access hooks
export const useAlertRepository = () => useServiceContainer().alertRepository;
export const useAuthRepository = () => useServiceContainer().authRepository;
export const useEventRepository = () => useServiceContainer().eventRepository;
export const useLocationRepository = () => useServiceContainer().locationRepository;
export const useOrganizationRepository = () => useServiceContainer().organizationRepository;
export const useSensorRepository = () => useServiceContainer().sensorRepository;
export const useUserRepository = () => useServiceContainer().userRepository;